import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Copy, ExternalLink, Trash2, ClipboardCheck, ArrowRight, FormInput, LayoutList, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../utils/helpers';
import FormFunnelTimeseries from '../components/FormFunnelTimeseries';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESET_OPTIONS = [
  { key: 'general', label: 'General' },
  { key: 'health', label: 'Health' },
  { key: 'life', label: 'Life' },
  { key: 'motor', label: 'Motor' },
  { key: 'workers_comp', label: 'Workers Comp' },
  { key: 'other', label: 'Other' },
];

const emptyForm = {
  name: '', description: '', header_title: '',
  thank_you_message: 'Thanks! Your details have been submitted.',
  template_preset: 'general', enabled_fields: [],
};

const publicUrlFor = (slug) => `${window.location.origin}/f/${slug}`;

const LeadForms = () => {
  const [forms, setForms] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [presets, setPresets] = useState({ presets: {}, optional_fields: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('forms');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [convertingId, setConvertingId] = useState('');
  const [analytics, setAnalytics] = useState({}); // { form_id: { views, submissions, converted } }
  const [expandedId, setExpandedId] = useState(''); // form_id currently drilled-in

  const load = useCallback(async () => {
    try {
      const [f, p, s] = await Promise.all([
        axios.get(`${API}/lead-forms`, { withCredentials: true }),
        axios.get(`${API}/lead-forms/presets`, { withCredentials: true }),
        axios.get(`${API}/lead-form-submissions`, { withCredentials: true }),
      ]);
      setForms(f.data || []);
      setPresets(p.data || { presets: {}, optional_fields: [] });
      setSubmissions(s.data || []);
    } catch (e) {
      toast.error('Failed to load forms');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load analytics per form (fire-in-parallel, keyed by form_id)
  useEffect(() => {
    if (!forms.length) return;
    Promise.all(
      forms.map(f =>
        axios.get(`${API}/lead-forms/${f.form_id}/analytics`, { withCredentials: true })
          .then(r => [f.form_id, r.data])
          .catch(() => [f.form_id, null])
      )
    ).then(entries => {
      const map = {};
      entries.forEach(([id, data]) => { if (data) map[id] = data; });
      setAnalytics(map);
    });
  }, [forms]);

  const choosePreset = (key) => {
    setForm(f => ({
      ...f,
      template_preset: key,
      enabled_fields: presets.presets?.[key] || [],
    }));
  };

  const toggleField = (field) => {
    setForm(f => {
      const set = new Set(f.enabled_fields);
      set.has(field) ? set.delete(field) : set.add(field);
      return { ...f, enabled_fields: Array.from(set) };
    });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Give the form a name'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/lead-forms`, form, { withCredentials: true });
      toast.success('Form created — shareable URL ready');
      setDialogOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this form? Existing submissions will be preserved.')) return;
    try {
      await axios.delete(`${API}/lead-forms/${id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Delete failed'); }
  };

  const copyLink = (slug) => {
    const url = publicUrlFor(slug);
    navigator.clipboard.writeText(url).then(
      () => toast.success('Public link copied to clipboard'),
      () => toast.error('Could not copy — copy manually: ' + url),
    );
  };

  const convertToLead = async (sub) => {
    setConvertingId(sub.submission_id);
    try {
      const { data } = await axios.post(`${API}/lead-form-submissions/${sub.submission_id}/convert`, {}, { withCredentials: true });
      toast.success('Converted to lead');
      // update local
      setSubmissions(list => list.map(s => s.submission_id === sub.submission_id ? { ...s, converted_lead_id: data.lead_id } : s));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Convert failed');
    } finally { setConvertingId(''); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="spinner" /></div>;

  const totalSubmissions = submissions.length;

  return (
    <div className="space-y-6" data-testid="lead-forms-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Data Collection Forms</h1>
          <p className="text-slate-600 mt-1">Shareable public forms to collect lead details</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setForm(emptyForm); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-800 hover:bg-blue-900" data-testid="new-form-button"><Plus className="h-4 w-4 mr-1.5" /> New Form</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>New Data Collection Form</DialogTitle>
              <DialogDescription>Choose a preset, tweak the optional fields, share the public link.</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1 col-span-2"><Label>Form Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Health Checkup Enquiry" data-testid="form-name-input" />
                </div>
                <div className="space-y-1"><Label>Header Title</Label>
                  <Input value={form.header_title} onChange={(e) => setForm({ ...form, header_title: e.target.value })} placeholder={form.name || 'Shown on public form'} data-testid="form-header-input" />
                </div>
                <div className="space-y-1"><Label>Template Preset</Label>
                  <Select value={form.template_preset} onValueChange={choosePreset}>
                    <SelectTrigger data-testid="form-preset-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRESET_OPTIONS.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 col-span-2"><Label>Description</Label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional context shown below the header" data-testid="form-desc-input" />
                </div>
                <div className="space-y-1 col-span-2"><Label>Thank-You Message</Label>
                  <Input value={form.thank_you_message} onChange={(e) => setForm({ ...form, thank_you_message: e.target.value })} data-testid="form-thanks-input" />
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-sm font-semibold text-slate-800 mb-2">Optional fields on the public form</p>
                <p className="text-[11px] text-slate-500 mb-2">Name and Phone are always required. Toggle any additional fields below.</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                  {(presets.optional_fields || []).map(f => (
                    <label key={f} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded border cursor-pointer ${form.enabled_fields.includes(f) ? 'border-blue-400 bg-blue-50' : 'border-slate-200 bg-white'}`} data-testid={`field-toggle-${f}`}>
                      <input type="checkbox" checked={form.enabled_fields.includes(f)} onChange={() => toggleField(f)} />
                      <span className="capitalize">{f.replace(/_/g, ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={save} disabled={saving} className="bg-blue-800 hover:bg-blue-900" data-testid="form-save-button">
                  {saving ? 'Saving…' : 'Create Form'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="form-stats">
        <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Total Forms</p>
            <FormInput className="h-4 w-4 text-blue-700" />
          </div>
          <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{forms.length}</p>
        </div>
        <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Total Submissions</p>
            <LayoutList className="h-4 w-4 text-emerald-700" />
          </div>
          <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{totalSubmissions}</p>
        </div>
      </div>

      <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5" data-testid="lead-forms-tabs">
        <button onClick={() => setTab('forms')} className={`px-3 py-1.5 rounded text-xs font-medium ${tab === 'forms' ? 'bg-white shadow-sm text-blue-800' : 'text-slate-600'}`} data-testid="tab-forms">Forms</button>
        <button onClick={() => setTab('submissions')} className={`px-3 py-1.5 rounded text-xs font-medium ${tab === 'submissions' ? 'bg-white shadow-sm text-blue-800' : 'text-slate-600'}`} data-testid="tab-submissions">Submissions ({totalSubmissions})</button>
      </div>

      {tab === 'forms' && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Your Forms ({forms.length})</CardTitle></CardHeader>
          <CardContent>
            {forms.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No forms yet. Create your first one to start collecting leads publicly.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Preset</TableHead>
                    <TableHead>Fields</TableHead>
                    <TableHead>Funnel · views → submits → leads</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {forms.map(f => {
                    const a = analytics[f.form_id];
                    const submitRate = a?.submit_rate_percent;
                    const convertRate = a?.convert_rate_percent;
                    const isOpen = expandedId === f.form_id;
                    return (
                    <React.Fragment key={f.form_id}>
                    <TableRow data-testid={`form-row-${f.form_id}`}>
                      <TableCell className="font-medium">{f.name}</TableCell>
                      <TableCell><Badge variant="outline">{f.template_preset}</Badge></TableCell>
                      <TableCell className="text-xs text-slate-500">{(f.enabled_fields || []).length} optional</TableCell>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => setExpandedId(isOpen ? '' : f.form_id)}
                          className="flex items-center gap-1.5 text-xs hover:bg-slate-50 rounded px-1 py-0.5"
                          data-testid={`form-funnel-${f.form_id}`}
                          title="Click to see the 14-day trend"
                        >
                          {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{a?.views ?? 0}</span>
                          <span className="text-slate-400">→</span>
                          <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">
                            {a?.submissions ?? 0}
                            {submitRate !== null && submitRate !== undefined && <span className="ml-1 text-[10px] text-blue-600">({submitRate}%)</span>}
                          </span>
                          <span className="text-slate-400">→</span>
                          <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                            {a?.converted ?? 0}
                            {convertRate !== null && convertRate !== undefined && <span className="ml-1 text-[10px] text-emerald-600">({convertRate}%)</span>}
                          </span>
                        </button>
                      </TableCell>
                      <TableCell>{f.created_at ? formatDate(f.created_at) : '—'}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => copyLink(f.slug)} data-testid={`form-copy-${f.form_id}`}>
                          <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => window.open(publicUrlFor(f.slug), '_blank')} data-testid={`form-open-${f.form_id}`}>
                          <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => del(f.form_id)} data-testid={`form-delete-${f.form_id}`}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow data-testid={`form-drilldown-${f.form_id}`}>
                        <TableCell colSpan={6} className="bg-slate-50/60 p-3">
                          <FormFunnelTimeseries formId={f.form_id} />
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'submissions' && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Submissions ({submissions.length})</CardTitle></CardHeader>
          <CardContent>
            {submissions.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No form submissions yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Received</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.map(s => (
                    <TableRow key={s.submission_id} data-testid={`submission-row-${s.submission_id}`}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell>{s.phone}</TableCell>
                      <TableCell>{s.email || '—'}</TableCell>
                      <TableCell>{s.created_at ? formatDate(s.created_at) : '—'}</TableCell>
                      <TableCell>
                        {s.converted_lead_id
                          ? <Badge className="bg-emerald-100 text-emerald-800"><ClipboardCheck className="h-3 w-3 mr-1" />Converted</Badge>
                          : <Badge className="bg-slate-100 text-slate-700">New</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        {s.converted_lead_id
                          ? <span className="text-[11px] text-slate-500">Lead created</span>
                          : (
                            <Button size="sm" onClick={() => convertToLead(s)} disabled={convertingId === s.submission_id} className="bg-blue-800 hover:bg-blue-900" data-testid={`submission-convert-${s.submission_id}`}>
                              <ArrowRight className="h-3.5 w-3.5 mr-1" /> {convertingId === s.submission_id ? 'Converting…' : 'Convert to Lead'}
                            </Button>
                          )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LeadForms;
